import { useEffect, useRef, useState } from 'react';
import { Check, Columns2, Monitor, Moon, PanelTop, Rows3, Sun, Building2, RotateCcw, PanelLeft } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/auth/AuthContext';
import { Dialog } from '@/ui/Dialog';
import { cn } from '@/lib/cn';
import { DESIGNS, designOf, type DesignId, type Density, type Mode } from '@/theme/designs';
import { useTheme, type Prefs } from '@/theme/ThemeProvider';

const LAYOUT_AD = { sidebar: ['Sol menü', PanelLeft], rail: ['İkon rayı', Columns2], topnav: ['Üst menü', PanelTop] } as const;

function Segmented<T extends string>({ value, onChange, items }: { value: T; onChange: (v: T) => void; items: [T, string, any][] }) {
  return (
    <div className="seg inline-flex p-1">
      {items.map(([v, t, I]) => (
        <button key={v} type="button" onClick={() => onChange(v)} className={cn('seg-item flex items-center gap-1.5 px-3 py-1.5 text-[12.5px] font-medium', value === v && 'is-on')}>
          {I && <I size={14} />}{t}
        </button>
      ))}
    </div>
  );
}

/** Tasarım / mod / yoğunluk seçimi. Seçimler anında önizlenir; "Kaydet" kullanıcıya özel olarak veritabanına yazar. */
export function AppearancePanel({ onDone, compact }: { onDone?: () => void; compact?: boolean }) {
  const { user } = useAuth();
  const t = useTheme();
  const start = useRef<Prefs>(t.prefs);
  const [busy, setBusy] = useState(false);
  const admin = !!user?.flags?.Yonetici;
  const dirty = JSON.stringify(start.current) !== JSON.stringify(t.prefs);

  useEffect(() => () => {
    // kaydedilmeden kapatılırsa önizlemeyi geri al
    if (!saved.current) t.preview(start.current);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const saved = useRef(false);

  const save = async () => {
    setBusy(true);
    try {
      await t.save(t.prefs);
      saved.current = true;
      start.current = t.prefs;
      toast.success(`${designOf(t.prefs.tasarim).ad} tasarımı kaydedildi`, { description: 'Bu tercih yalnız sizin hesabınız için geçerli.' });
      onDone?.();
    } catch (e: any) { toast.error(e.message); } finally { setBusy(false); }
  };
  const company = async () => {
    setBusy(true);
    try {
      await t.saveCompany(t.prefs);
      toast.success('Şirket varsayılanı güncellendi', { description: 'Kişisel tercihi olmayan kullanıcılar bu görünümle açılır.' });
    } catch (e: any) { toast.error(e.message); } finally { setBusy(false); }
  };
  const reset = async () => {
    setBusy(true);
    try {
      await t.reset();
      saved.current = true;
      start.current = { ...t.prefs };
      toast.success('Şirket varsayılanına dönüldü');
      onDone?.();
    } catch (e: any) { toast.error(e.message); } finally { setBusy(false); }
  };
  const cancel = () => { t.preview(start.current); onDone?.(); };

  return (
    <div className="flex flex-col gap-5">
      <div>
        <div className="h-label mb-2">Tasarım</div>
        <div className={cn('grid gap-3', compact ? 'grid-cols-2 sm:grid-cols-3' : 'grid-cols-1 sm:grid-cols-2 xl:grid-cols-5')}>
          {DESIGNS.map((d) => {
            const on = t.prefs.tasarim === d.id;
            const [lay, LI] = LAYOUT_AD[d.layout];
            return (
              <button key={d.id} type="button" onClick={() => t.preview({ tasarim: d.id as DesignId, ...(t.prefs.mod === designOf(t.prefs.tasarim).dogalMod && d.dogalMod !== t.prefs.mod ? { mod: d.dogalMod } : {}) })} data-testid={`tasarim-${d.id}`}
                className={cn('design-card group relative overflow-hidden text-left', on && 'is-on')}>
                <div className="relative aspect-[16/10] overflow-hidden bg-[var(--fill)]">
                  <img src={`/design/${d.no}.jpg`} alt="" className="h-full w-full object-cover object-left-top transition duration-300 group-hover:scale-[1.04]" />
                  {on && <span className="absolute right-2 top-2 grid h-6 w-6 place-items-center rounded-full bg-[var(--accent)] text-white shadow"><Check size={14} strokeWidth={3} /></span>}
                </div>
                <div className="p-3">
                  <div className="flex items-center gap-2"><span className="font-semibold">{d.ad}</span><span className="ml-auto flex gap-0.5">{d.renkler.map((c) => <i key={c} className="h-3 w-3 rounded-full border border-black/10" style={{ background: c }} />)}</span></div>
                  {!compact && <div className="mt-1 line-clamp-2 text-xs muted">{d.ozet}</div>}
                  <div className="mt-1.5 flex items-center gap-1 text-[11px] muted"><LI size={12} />{lay}</div>
                </div>
              </button>
            );
          })}
        </div>
      </div>
      <div className="flex flex-wrap gap-x-8 gap-y-4">
        <div>
          <div className="h-label mb-2">Mod</div>
          <Segmented<Mode> value={t.prefs.mod} onChange={(mod) => t.preview({ mod })} items={[['acik', 'Açık', Sun], ['koyu', 'Koyu', Moon], ['sistem', 'Sistem', Monitor]]} />
        </div>
        <div>
          <div className="h-label mb-2">Yoğunluk</div>
          <Segmented<Density> value={t.prefs.yogunluk} onChange={(yogunluk) => t.preview({ yogunluk })} items={[['rahat', 'Rahat', null], ['normal', 'Normal', Rows3], ['sik', 'Sık', null]]} />
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2 border-t b-border pt-4">
        <div className="mr-auto text-xs muted">
          {t.server ? <>Şirket varsayılanı: <b className="text-[var(--text)]">{designOf(t.server.sirket.tasarim).ad}</b> · {t.server.sirket.mod === 'koyu' ? 'Koyu' : t.server.sirket.mod === 'sistem' ? 'Sistem' : 'Açık'}{t.server.kisisel ? ' · sizin kişisel tercihiniz var' : ''}</> : null}
        </div>
        {t.server?.kisisel && <button className="btn btn-ghost" disabled={busy} onClick={reset}><RotateCcw size={14} />Varsayılana dön</button>}
        {admin && <button className="btn" disabled={busy} onClick={company} title="Kişisel tercihi olmayan tüm kullanıcılar için"><Building2 size={14} />Şirket varsayılanı yap</button>}
        {onDone && <button className="btn" onClick={cancel}>Vazgeç</button>}
        <button className="btn btn-primary" disabled={busy || (!dirty && !!t.server?.kisisel)} onClick={save}><Check size={14} />Kaydet</button>
      </div>
    </div>
  );
}

export function AppearanceDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (o: boolean) => void }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange} title="Görünüm" description="Arayüz tasarımı, mod ve yoğunluk" className="w-[min(1180px,96vw)]">
      {open && <AppearancePanel onDone={() => onOpenChange(false)} />}
    </Dialog>
  );
}
